import { useEffect, useMemo, useRef } from 'react';
import { useActiveClassroom, type SubPageKey } from '../../context/ActiveClassroomProvider.js';
import { MascotImage } from '../../theme/index.js';
import { useTheme } from '../../theme/ThemeProvider.js';
import { getClassroomShowMascot } from '../../theme/themeStorage.js';
import { useClassroomSettings } from '../../hooks/useClassroomSettings.js';
import { Calendar } from '../Calendar/index.js';
import { DayOfSchool } from '../DayOfSchool/index.js';
import { Number as NumberPage } from '../Number/index.js';
import { Weather } from '../Weather/index.js';
import { Feelings } from '../Feelings/index.js';
import { Question } from '../Question/index.js';
import { Pledges } from '../Pledges/index.js';
import { YouTube } from '../YouTube/index.js';
import { Links } from '../Links/index.js';
import { Search } from '../Search/index.js';
import { Home } from '../Home/index.js';
import { RewardsPage } from '../../rewards/RewardsPage.js';
import styles from './LiveSurface.module.css';

// Order used for slide-direction calculation. Mirrors MmPageNav so left
// taps slide-from-left and right taps slide-from-right. Search lives
// off the strip and never slides.
const SUBPAGE_ORDER: SubPageKey[] = [
  'home',
  'calendar',
  'day-of-school',
  'number',
  'weather',
  'feelings',
  'question',
  'pledges',
  'rewards',
  'youtube',
  'links',
];

/**
 * Active morning-meeting surface. Picks the right page screen based on
 * the active classroom's current sub-page, layers a scene-aware
 * BgLayer, and decorates with the bottom MmPageNav, the top-left back
 * chevron, and the theme mascot. Page transitions slide left or right
 * depending on which direction in the bottom strip the user tapped.
 */
export function LiveSurface() {
  const { active, subPage } = useActiveClassroom();
  const theme = useTheme();
  const settings = useClassroomSettings(active?.id);
  const isOlderHome = settings.data?.homeVariant === 'g48';
  // Per-classroom "show mascot" toggle (sync localStorage read; cheap). The
  // 4–8 ("g48") home reclaims the mascot's space for its To-Do/Turn-In boxes,
  // so the mascot DEFAULTS off there — but an explicit teacher choice (on or
  // off) always wins. K–3 defaults on.
  const showMascot = active ? getClassroomShowMascot(active.id, !isOlderHome) : true;

  // The classroom's home-scene bg is rendered behind every page by
  // ClassroomBg; no per-page bg overlays. Earlier this switched to
  // calendar/day-of-school/number scenes, but Sarah wants every live
  // page to share the same themed backdrop so the room feels coherent.

  // Compute slide direction by comparing previous + current index.
  const prevSubPageRef = useRef<SubPageKey>(subPage);
  const direction: 'left' | 'right' | 'none' = useMemo(() => {
    const oldIdx = SUBPAGE_ORDER.indexOf(prevSubPageRef.current);
    const newIdx = SUBPAGE_ORDER.indexOf(subPage);
    if (oldIdx < 0 || newIdx < 0 || oldIdx === newIdx) return 'none';
    return newIdx > oldIdx ? 'right' : 'left';
  }, [subPage]);
  useEffect(() => {
    prevSubPageRef.current = subPage;
  }, [subPage]);

  let screen;
  if (subPage === 'search') screen = <Search />;
  else if (subPage === 'calendar') screen = <Calendar />;
  else if (subPage === 'day-of-school') screen = <DayOfSchool />;
  else if (subPage === 'number') screen = <NumberPage />;
  else if (subPage === 'weather') screen = <Weather />;
  else if (subPage === 'feelings') screen = <Feelings />;
  else if (subPage === 'question') screen = <Question />;
  else if (subPage === 'pledges') screen = <Pledges />;
  else if (subPage === 'rewards') screen = <RewardsPage />;
  else if (subPage === 'youtube') screen = <YouTube />;
  else if (subPage === 'links') screen = <Links />;
  else screen = <Home />;

  // Mascot only renders here (not inside Home anymore) so we can resize
  // and reposition consistently across pages. On the live home view it
  // stays big, anchored bottom-left (Sarah's v1 layout). On every
  // other page it shrinks and tucks into the upper-left corner so it
  // doesn't crowd the page chrome.
  // The mascot follows the show-mascot toggle everywhere (the 4–8 default is
  // baked into `showMascot` above). Big on the home page, tucked in the corner
  // elsewhere, hidden on the search overlay.
  const mascotMode: 'home' | 'corner' | 'hidden' = !showMascot
    ? 'hidden'
    : subPage === 'search'
      ? 'hidden'
      : subPage === 'home'
        ? 'home'
        : 'corner';

  return (
    <>
      <div
        key={subPage}
        className={styles.pageTransition}
        data-direction={direction}
        data-subpage={subPage}
      >
        {screen}
      </div>
      {mascotMode !== 'hidden' ? (
        <div
          className={
            mascotMode === 'home'
              ? `${styles.mascotHome} ${isOlderHome ? styles.mascotHomeOlder : ''}`
              : styles.mascotCorner
          }
          aria-hidden="true"
          data-theme-key={theme.manifest.key}
        >
          <MascotImage slot="home" className={styles.mascotImage ?? ''} />
        </div>
      ) : null}
    </>
  );
}
